/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { Button, Center, Group, Loader, Text, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconEye, IconTrash } from '@tabler/icons-react';
import { isEqual } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Chart, ChartsCriteria, EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import { getInitialChartCriteria, useAdminCharts, useAdminDeleteChart, useChartCriteriaUrlSync, useDeclarants } from '@admin/hooks';
import {
  CheckedColumn,
  ListFilterChangeEvent,
  ListFilterDropdown,
  ListFilterDropdownElements,
  SearchBox,
  Table,
  TableActions,
  TableColumnProps,
} from '@components';
import { ContentLayout } from '@features/layout';
import { removeUndefined } from '@utils';
import { EditChart } from './EditChart';

export const ManageCharts = () => {
  const theme = useMantineTheme();
  const [searchParams] = useSearchParams();
  const initialCriteria = getInitialChartCriteria(searchParams);

  const [filters, setFilters] = useState<ChartsCriteria['filters']>(initialCriteria.filters);
  const [sorting, setSorting] = useState<ChartsCriteria['sorting']>(initialCriteria.sorting);
  const [pagination, setPagination] = useState<ChartsCriteria['pagination']>(initialCriteria.pagination);
  useChartCriteriaUrlSync({ filters, sorting, pagination });
  const { data: declarantsList } = useDeclarants();
  const [declarantsOptions, setDeclarantsOptions] = useState<ListFilterDropdownElements>({});
  const { data: { data: { charts = [], totalItems = 0 } = {} } = {}, isLoading } = useAdminCharts({
    filters,
    sorting,
    pagination,
  });

  const deleteChartMutation = useAdminDeleteChart();

  useEffect(() => {
    setDeclarantsOptions(buildDeclarantsDropdownOptions(declarantsList));
  }, [declarantsList, filters.filterByDeclarants]);

  const buildDeclarantsDropdownOptions = (list) => {
    const declarantsDropdownOptionList: ListFilterDropdownElements = {};

    list?.forEach((dec) => {
      declarantsDropdownOptionList[dec._id] = {
        label: dec.accountName,
        checked: filters.filterByDeclarants ? filters.filterByDeclarants.includes(dec._id) : true,
      };
    });

    return declarantsDropdownOptionList;
  };

  const setNewFilter = (field: string, value: string | string[]) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...{ [field]: value },
    }) as ChartsCriteria['filters'];

    setFilters(mergedFilters);
    setPagination({ ...pagination, page: 1 });
  };

  const filterByDeclarant = (declarantOption: ListFilterChangeEvent) => {
    if (!declarantsList) {
      return;
    }

    let filterByDeclarants: string[];
    if (!declarantOption.id) {
      if (declarantOption.checked) {
        filterByDeclarants = declarantsList.map((usr) => usr._id);
      } else {
        filterByDeclarants = [];
      }
    } else {
      filterByDeclarants = declarantsList.reduce((acc, cur) => {
        if ((declarantOption.id === cur._id && declarantOption.checked) || (filters.filterByDeclarants?.includes(cur._id) && declarantOption.id !== cur._id)) {
          acc.push(cur._id);
        }
        return acc;
      }, []);
    }

    if (!isEqual(filterByDeclarants, filters.filterByDeclarants)) {
      setNewFilter('filterByDeclarants', filterByDeclarants);
    }
  };

  const openDeleteChart = (chart: Chart) => {
    modals.openConfirmModal({
      title: 'Delete Chart',
      children: <Text>Are you sure you want to delete this chart?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteChartMutation.mutate({ params: { id: chart._id }, body: undefined });
      },
    });
  };

  const openChartModal = (chart?: Chart) => {
    modals.open({
      title: chart ? 'Edit Chart' : 'New Chart',
      children: <EditChart chart={chart} onHide={() => modals.closeAll()} />,
      size: 'lg',
    });
  };

  const handleSearch = (searchQuery: string) => {
    setFilters((prev) => ({
      ...prev,
      searchQuery,
    }));
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const columns: TableColumnProps<Chart>[] = [
    {
      name: 'Order',
      cell: (row) => row.order,
      sortable: true,
      selector: 'order',
    },
    {
      name: 'Name',
      cell: (row) => row.name,
      sortable: true,
      selector: 'name',
    },
    {
      name: 'For Supervisor Only',
      cell: (row) => <CheckedColumn checked={row.forSupervisorOnly} />,
    },
    {
      name: 'Type',
      cell: (row) => (row?.isChart ? 'Chart' : 'Report'),
    },
    {
      name: 'Chart ID',
      cell: (row) => row.chartId,
    },
    {
      name: 'Updated At',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT),
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row) => (
        <TableActions
          actions={[
            {
              iconComponent: <IconEye color={theme.colors[theme.primaryColor][6]} />,
              text: 'View Chart',
              onClick: () => openChartModal(row),
            },
            {
              iconComponent: <IconTrash color={theme.colors[theme.primaryColor][6]} />,
              text: 'Delete',
              onClick: () => openDeleteChart(row),
            },
          ]}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Charts</ContentLayout.Title>
        <ContentLayout.Filters>
          <Button onClick={() => openChartModal()} variant="outline" size="lg">
            Add new
          </Button>
          <SearchBox placeholder="Search by chart name" handleSearch={handleSearch} value={filters?.searchQuery} />
        </ContentLayout.Filters>
      </ContentLayout.Header>
      <ContentLayout.SubHeader>
        <Group justify="flex-end" mb="lg">
          <ListFilterDropdown title="Declarant filter" values={declarantsOptions} onChange={filterByDeclarant} />
        </Group>
      </ContentLayout.SubHeader>
      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={(charts as Chart[]) || []}
          total={totalItems}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
          verticalSpacing="xs"
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
